import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/route-paths';
import type { StripeConnectStatus } from '@borradh-workspace/api-client/types';
import { Link } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
// Narrow import (not the '../api' barrel), same reason as the panel's.
import { useGetAccountStatus } from '../api/get-account-status';

type BookingPaymentWarning = NonNullable<
  StripeConnectStatus['bookingPaymentWarning']
>;

const ACCOUNT_PROBLEM: Record<BookingPaymentWarning['reason'], string> = {
  not_connected: 'Stripe isn’t connected',
  inactive: 'Your Stripe connection is inactive',
  charges_disabled: 'Your Stripe account can’t accept payments right now',
};

/**
 * The sentences the banner shows. Counts services rather than naming them.
 * Exported for the copy tests.
 */
export function bookingPaymentWarningCopy(warning: BookingPaymentWarning) {
  const problem = ACCOUNT_PROBLEM[warning.reason];
  const { serviceCount, defaultPolicyRequiresPayment } = warning;
  // No account at all: the server books these services without the payment
  // (pay in clinic) rather than refusing them. The clinic is losing deposits,
  // not bookings.
  if (warning.reason === 'not_connected') {
    if (serviceCount === 0) {
      return {
        title: 'New services will be booked without payment',
        body: `${problem}, but your default payment policy asks customers to pay when they book, so services that use it are booked online without that payment and paid in clinic instead.`,
        action:
          'Connect Stripe to collect it, or change your default payment policy.',
      };
    }
    const alsoDefault = defaultPolicyRequiresPayment
      ? ' Your default payment policy asks for payment too, so new services will be booked the same way.'
      : '';
    return serviceCount === 1
      ? {
          title: 'A service is being booked without its payment',
          body: `${problem}, so one service that requires payment when booking is booked online without it. The payment is collected in clinic instead.${alsoDefault}`,
          action:
            'Connect Stripe to collect it, or switch it to payment in clinic.',
        }
      : {
          title: `${serviceCount} services are being booked without payment`,
          body: `${problem}, so ${serviceCount} services that require payment when booking are booked online without it. The payment is collected in clinic instead.${alsoDefault}`,
          action:
            'Connect Stripe to collect it, or switch these services to payment in clinic.',
        };
  }
  if (serviceCount === 0) {
    return {
      title: 'New services can’t be booked online',
      body: `${problem}, but your default payment policy asks customers to pay when they book, so they can’t book services that use it online.`,
      action:
        'Fix your payment settings, or change your default payment policy.',
    };
  }
  const alsoDefault = defaultPolicyRequiresPayment
    ? ' Your default payment policy asks for payment too, so new services will be affected.'
    : '';
  return serviceCount === 1
    ? {
        title: 'Customers can’t book a service online',
        body: `${problem}, so customers can’t book one service online, because it requires payment when booking.${alsoDefault}`,
        action: 'Fix your payment settings, or switch it to payment in clinic.',
      }
    : {
        title: `Customers can’t book ${serviceCount} services online`,
        body: `${problem}, so customers can’t book ${serviceCount} services online, because they require payment when booking.${alsoDefault}`,
        action:
          'Fix your payment settings, or switch these services to payment in clinic.',
      };
}

/**
 * A service that requires payment is booked online
 * WITHOUT that payment while the clinic has no Stripe account at all, and is
 * unbookable online while the account it has cannot charge. Nothing else says
 * either. Stripe can break long after the service was set up (a disconnect, or
 * `account.updated` turning charges off), so this reads the live answer from
 * the account status the server computes on every fetch.
 */
export function BookingPaymentWarningBanner({
  showSettingsLink = true,
}: {
  /** Off on the payments page itself. */
  showSettingsLink?: boolean;
}) {
  const { status } = useGetAccountStatus();
  const warning = status?.bookingPaymentWarning;
  if (!warning) return null;

  const { title, body, action } = bookingPaymentWarningCopy(warning);
  return (
    <Alert
      variant={warning.reason === 'not_connected' ? 'default' : 'destructive'}
      className="gap-y-1 py-4"
    >
      <TriangleAlert className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-col gap-3 leading-relaxed">
        <span>
          {body} {action}
        </span>
        {showSettingsLink && (
          <div className="pt-0.5">
            <Button asChild size="sm" variant="outline">
              <Link to={ROUTES.settingsPayments}>Go to payment settings</Link>
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
